import * as React from "react";
import cx from "classnames";
import { Flex } from "@common/components/Flex";
import { Hint } from "@common/components/hint";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";

import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { TableFieldProps, useTableFieldController } from "./table-field.controller";
import { HeaderColumn } from "./components/column";
import { FieldContainer } from "../field-container";
import { Button } from "@common/components/Button";
import { Row } from "./components/row";

import "./table-field.styles.scss";

export const TableField: React.FC<TableFieldProps> = React.memo((props) => {
  const { datasheetElement } = props;

  const containerRef = React.useRef<HTMLDivElement>(null);

  const {
    selectedItemId,
    currentValue,
    footerActions,
    headingReference,
    headingPlacedHorizontally,
    headingSize,
    headingPosition,
    handleResizeColumn,
    handleRowClick,
  } = useTableFieldController(props);

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext}
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
    />
  );

  return (
    <DatasheetItem
      className="DatasheetsResultDatasheetTableField"
      style={{
        top:
          datasheetElement.coordinates.y -
          (datasheetElement.original.headpos === DatasheetFieldComponentHeadingPosition.top ? headingSize.height : 0),
        left:
          datasheetElement.coordinates.x -
          (datasheetElement.original.headpos === DatasheetFieldComponentHeadingPosition.left ? headingSize.width : 0),
        display: headingPlacedHorizontally ? "flex" : "block",
        alignItems: headingPlacedHorizontally ? "flex-start" : undefined,
      }}
    >
      {[DatasheetFieldComponentHeadingPosition.top, DatasheetFieldComponentHeadingPosition.left].includes(
        headingPosition
      ) && labelComponent}
      <Flex>
        <FieldContainer
          width={datasheetElement.original.width}
          height={datasheetElement.original.height}
          showBorder={datasheetElement.original.showborder}
        >
          <div 
            className={cx(
              "DatasheetsResultDatasheetTableField__table", 
            )}
          >
            <Flex className="DatasheetsResultDatasheetTableField__table__header" reference={containerRef}>
              {datasheetElement.original.field.subfields?.map((field) => (
                <HeaderColumn
                  width={100}
                  // width={field.width}
                  onResizeEnd={handleResizeColumn}
                  title={field.fieldname}
                  containerRef={containerRef}
                  key={field.fieldid}
                  field={field}
                />
              ))}
            </Flex>
            <div className="DatasheetsResultDatasheetTableField__table__body">
              {currentValue?.map((dataItem) => (
                <Row 
                  selected={selectedItemId === dataItem.id}
                  onClick={handleRowClick}
                  key={dataItem.id}
                  columns={datasheetElement.original.field.subfields || []}
                  data={dataItem}
                />
              ))}
            </div>
            <Flex className="DatasheetsResultDatasheetTableField__table__footer">
              {footerActions.map((action) => (
                <Button 
                  disabled={action.disabled}
                  style={{ marginRight: 8 }}
                  variant="text"
                  key={action.label}
                  text={<><img src="gif/row_add_inactive.gif" />{action.label}</>}
                  onClick={action.onClick}
                />
              ))}
            </Flex>
          </div>
        </FieldContainer>
        {datasheetElement.original.helptext && (
          <Hint description={datasheetElement.original.helptext} />
        )}
      </Flex>
      {[DatasheetFieldComponentHeadingPosition.bottom, DatasheetFieldComponentHeadingPosition.right].includes(
        headingPosition
      ) && labelComponent}
    </DatasheetItem>
  );
});
